import type { HoverIdlePaymentState } from "@/features/payment/types";
import {
  compareHuge,
  deserializeHuge,
  huge,
  hugeOne,
  isNonNegativeHuge,
  type HugeNumber,
} from "@/shared/numbers";
import {
  createDefaultNHovState,
  migrateLegacyNHovBranchEffectOverrides,
} from "../nHov/nHovLogic";
import {
  createDefaultOHovState,
  isOHovUnlocked,
  normalizeOHovRunRecords,
} from "../oHov/oHovLogic";
import {
  normalizeNHovBranchExperienceByBranch,
  normalizeLegacyNHovBranchEffectOverrides,
  normalizeNHovContinuousEffectProgressByBranch,
  normalizeNHovEffectBoundaryCaps,
} from "../nHov/nHovBranches";
import {
  normalizeMpmState,
  withMpmAmplifierPanelsUnlocked,
  withMpmAmplifierUpgradesUnlocked,
  withMpmHovGainUpgradesUnlocked,
  withMpmMHovExecutionUnlocked,
  withMpmNHovExecutionUnlocked,
  withMpmOHovExecutionUnlocked,
} from "../mpm/mpmState";
import { normalizeHovAmplifierState } from "../hov/hovAmplifierModel";
import type {
  HoverIdleCumulativeStatsState,
  HoverIdleModelState,
  MHovState,
  NHovState,
  OHovState,
} from "../types";
import { createDefaultMHovState } from "../mHov/mHovLogic";
import { defaultHoverIdleConfig, defaultSettings } from "./hoverIdleDefaults";
import { normalizePaymentState } from "./hoverIdleStateClone";
import { resolveAmplifierUpgradeGuardMs } from "./hoverIdleSettingsValues";
import {
  HOV_CELL_COUNT,
  TIM_CELL_BASE_COSTS,
  createDefaultHovGainUnlockCosts,
} from "./hoverIdleState";
import { normalizeResetAutomationState } from "./resetAutomationMinimumIntervals";
import { normalizeExplorationState } from "../exploration/explorationState";

export interface HoverIdleModelRestoreInput {
  readonly state: HoverIdleModelState;
  readonly fallbackNowMs: number;
  readonly existingMpmHovGainUpgradesUnlocked: boolean;
  readonly existingMpmAmplifierPanelsUnlocked: boolean;
  readonly existingMpmAmplifierUpgradesUnlocked: boolean;
  readonly existingMpmMHovExecutionUnlocked: boolean;
  readonly existingMpmNHovExecutionUnlocked: boolean;
  readonly existingMpmOHovExecutionUnlocked: boolean;
  readonly mpCapacityInitialized: boolean;
}

export interface HoverIdleModelRestoreResult {
  readonly state: HoverIdleModelState;
  readonly paymentState: HoverIdlePaymentState;
  readonly postRestoreSyncPlan: HoverIdlePostRestoreSyncPlan;
}

export interface HoverIdlePostRestoreSyncPlan {
  readonly recomputeMainResourceGains: true;
  readonly resolveUnlockState: true;
  readonly syncTimRootCellState: true;
  readonly trimMpAssignmentsToCapacity: boolean;
  readonly syncThresholdUnlocks: true;
}

export function normalizeHoverIdleModelRestoreState(
  input: HoverIdleModelRestoreInput,
): HoverIdleModelRestoreResult {
  const stateWithNormalizedValues = normalizeHoverIdleModelStateValues(
    input.state,
    input.fallbackNowMs,
  );
  const paymentState = normalizePaymentState(
    (stateWithNormalizedValues as { payment?: Partial<HoverIdlePaymentState> })
      .payment,
  );

  const normalizedState: HoverIdleModelState = {
    ...stateWithNormalizedValues,
    economy: {
      ...stateWithNormalizedValues.economy,
      mpm: withMpmAmplifierPanelsUnlocked(
        withMpmAmplifierUpgradesUnlocked(
          withMpmOHovExecutionUnlocked(
            withMpmNHovExecutionUnlocked(
              withMpmMHovExecutionUnlocked(
                withMpmHovGainUpgradesUnlocked(
                  normalizeMpmState(stateWithNormalizedValues.economy.mpm),
                  input.existingMpmHovGainUpgradesUnlocked,
                ),
                input.existingMpmMHovExecutionUnlocked,
              ),
              input.existingMpmNHovExecutionUnlocked,
            ),
            input.existingMpmOHovExecutionUnlocked,
          ),
          input.existingMpmAmplifierUpgradesUnlocked,
        ),
        input.existingMpmAmplifierPanelsUnlocked,
      ),
    },
    payment: paymentState,
  };

  return {
    state: normalizedState,
    paymentState,
    postRestoreSyncPlan: resolveHoverIdlePostRestoreSyncPlan({
      mpCapacityInitialized: input.mpCapacityInitialized,
    }),
  };
}

export function resolveHoverIdlePostRestoreSyncPlan(options: {
  readonly mpCapacityInitialized: boolean;
}): HoverIdlePostRestoreSyncPlan {
  return {
    recomputeMainResourceGains: true,
    resolveUnlockState: true,
    syncTimRootCellState: true,
    trimMpAssignmentsToCapacity: options.mpCapacityInitialized,
    syncThresholdUnlocks: true,
  };
}

function normalizeHoverIdleModelStateValues(
  state: HoverIdleModelState,
  fallbackNowMs: number,
): HoverIdleModelState {
  const mHov = state.economy.mHov ?? createDefaultMHovState();
  const nHov = state.economy.nHov ?? createDefaultNHovState();
  const oHov = state.economy.oHov ?? createDefaultOHovState(fallbackNowMs);
  const normalizedHov = deserializeHuge(state.economy.hov);

  const normalizedTimUpgradeCost = deserializeHuge(
    state.economy.timUpgradeCost,
  );
  const normalizedTimGainPerSecond = deserializeHuge(
    state.economy.timGainPerSecond,
  );

  return {
    ...state,
    settings: normalizeHoverIdleSettingsState(state.settings),
    economy: {
      ...state.economy,
      isTimHovActivationEnabled:
        (state.economy as Partial<HoverIdleModelState["economy"]>)
          .isTimHovActivationEnabled ?? true,
      hov: normalizedHov,
      tim: deserializeHuge(state.economy.tim),
      hovGainPerSecond: deserializeHuge(state.economy.hovGainPerSecond),
      timGainPerSecond: isPositiveHuge(normalizedTimGainPerSecond)
        ? normalizedTimGainPerSecond
        : huge(defaultHoverIdleConfig.timGainPerSecond),
      hiddenGrowth: {
        ...state.economy.hiddenGrowth,
        hovGainBonus: deserializeHuge(state.economy.hiddenGrowth.hovGainBonus),
        timGainBonus: deserializeHuge(state.economy.hiddenGrowth.timGainBonus),
      },
      upgradeCost: deserializeHuge(state.economy.upgradeCost),
      timUpgradeCost: isPositiveHuge(normalizedTimUpgradeCost)
        ? normalizedTimUpgradeCost
        : huge(defaultHoverIdleConfig.timUpgradeCost),
      targetGrid: {
        ...state.economy.targetGrid,
        hovCells: state.economy.targetGrid.hovCells.map((cell) => ({
          ...cell,
          hovGainMultiplier: deserializeHuge(cell.hovGainMultiplier),
          upgradeCost: deserializeHuge(cell.upgradeCost),
        })),
      },
      minHovGainUnlockCostsByTargetIndex: normalizeHovGainUnlockCosts(
        (state.economy as Partial<HoverIdleModelState["economy"]>)
          .minHovGainUnlockCostsByTargetIndex,
      ),
      minHovGainMilestoneTriggerCostsByMilestoneIndex:
        normalizeHovGainMilestoneTriggerCosts(
          (state.economy as Partial<HoverIdleModelState["economy"]>)
            .minHovGainMilestoneTriggerCostsByMilestoneIndex,
        ),
      timTargetGrid: {
        ...state.economy.timTargetGrid,
        timCells: state.economy.timTargetGrid.timCells.map((cell, index) => {
          const normalizedCost = deserializeHuge(cell.upgradeCost);
          return {
            ...cell,
            timGainMultiplier: deserializeHuge(cell.timGainMultiplier),
            upgradeCost: isPositiveHuge(normalizedCost)
              ? normalizedCost
              : huge(TIM_CELL_BASE_COSTS[index] ?? 1),
          };
        }),
      },
      mHov: normalizeMHovState(mHov),
      nHov: normalizeNHovState(nHov),
      oHov: normalizeOHovState(oHov, normalizedHov, fallbackNowMs),
      resetAutomation: normalizeResetAutomationState(
        state.economy.resetAutomation,
      ),
      lastAnyResetAtMs: normalizeFiniteNonNegativeNumber(
        (state.economy as Partial<HoverIdleModelState["economy"]>)
          .lastAnyResetAtMs,
        0,
      ),
      hovAmplifier: normalizeHovAmplifierState(state.economy.hovAmplifier),
      exploration: normalizeExplorationState(state.economy.exploration),
    },
    cumulativeStats: {
      ...state.cumulativeStats,
      totalHovEarned: deserializeHuge(state.cumulativeStats.totalHovEarned),
      totalTimEarned: deserializeHuge(state.cumulativeStats.totalTimEarned),
      totalTimSpent: deserializeHuge(state.cumulativeStats.totalTimSpent),
      lifetimeHovRefinedFromTim: deserializeHuge(
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .lifetimeHovRefinedFromTim,
      ),
      hovGainFamilyUpgradesPurchased: normalizeNonNegativeInteger(
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .hovGainFamilyUpgradesPurchased,
        deriveHovGainFamilyUpgradesPurchased(state),
      ),
      amplifierUpgradesPurchased: normalizeNonNegativeInteger(
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .amplifierUpgradesPurchased,
        0,
      ),
      maxHov: deserializeHuge(state.cumulativeStats.maxHov),
      // Additive record: legacy saves omit it, so default to false.
      hasExecutedNewHovWithoutMergeHov:
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .hasExecutedNewHovWithoutMergeHov === true,
      // Additive record: legacy saves omit it, so default to false.
      hasExecutedMergeHovWithOnlyOneHovTargetCell:
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .hasExecutedMergeHovWithOnlyOneHovTargetCell === true,
      // Additive record: legacy saves omit it, so default to false.
      hasExecutedNHovExceedingNotFullyFreeThreshold:
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .hasExecutedNHovExceedingNotFullyFreeThreshold === true,
      // Additive record: legacy saves omit it, so default to false.
      hasExecutedMassiveMergeHov:
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .hasExecutedMassiveMergeHov === true,
      // Additive record: legacy saves omit it, so default to false.
      hasExecutedOutsideMhov:
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .hasExecutedOutsideMhov === true,
      // Additive record: legacy saves omit it, so default to false.
      hasExecutedOutsideNhov:
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .hasExecutedOutsideNhov === true,
      // Additive record: legacy saves omit it, so default to false.
      redAmplifierFullChargeReached:
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .redAmplifierFullChargeReached === true,
      totalHovCoreSpent: deserializeHuge(
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .totalHovCoreSpent,
      ),
      // Additive record: legacy saves omit it, restore defaults it to 0.
      mhovExecutedByMpCount: normalizeNonNegativeInteger(
        (state.cumulativeStats as Partial<HoverIdleCumulativeStatsState>)
          .mhovExecutedByMpCount,
        0,
      ),
    },
  };
}

function normalizeHoverIdleSettingsState(
  settings: HoverIdleModelState["settings"],
): HoverIdleModelState["settings"] {
  const source = settings as Partial<HoverIdleModelState["settings"]>;
  return {
    ...settings,
    lowPowerMode: source.lowPowerMode ?? defaultSettings.lowPowerMode,
    amplifierUpgradeGuardMs: resolveAmplifierUpgradeGuardMs(
      source.amplifierUpgradeGuardMs,
    ),
    hasSeenAvailableAmplifierUpgradeGuard:
      source.hasSeenAvailableAmplifierUpgradeGuard === true,
    hasSeenMHovButton: source.hasSeenMHovButton === true,
    hasSeenNHovButton: source.hasSeenNHovButton === true,
    outsideResetBurstAnimationEnabled:
      source.outsideResetBurstAnimationEnabled ??
      defaultSettings.outsideResetBurstAnimationEnabled,
    hoverToggleDuration:
      source.hoverToggleDuration ?? defaultSettings.hoverToggleDuration,
    hoverChargeDuration:
      source.hoverChargeDuration ?? defaultSettings.hoverChargeDuration,
  };
}

function normalizeMHovState(state: MHovState): MHovState {
  return {
    ...state,
    hgMultiplier: normalizeMHovHgMultiplier(
      (state as Partial<MHovState>).hgMultiplier,
    ),
    currentLoopHovEarned: deserializeHuge(state.currentLoopHovEarned),
    maxHovSinceLastMHov: deserializeHuge(state.maxHovSinceLastMHov),
    lastRewardBaseHov: deserializeHuge(
      (state as Partial<MHovState>).lastRewardBaseHov,
    ),
  };
}

// MHov HG multiplier must stay a positive HugeNumber; a missing or non-positive
// value (legacy / malformed state) falls back to the neutral 1x multiplier.
function normalizeMHovHgMultiplier(value: unknown): HugeNumber {
  const multiplier = deserializeHuge(value);
  return compareHuge(multiplier, 0) <= 0 ? hugeOne() : multiplier;
}

function normalizeNHovState(state: NHovState): NHovState {
  const legacyState = state as NHovState & {
    readonly branchEffectOverrides?: Readonly<
      Partial<
        Record<
          "hgAmplify" | "hgDiscount" | "hngScale" | "hgNHovEffectsToHng",
          unknown
        >
      >
    >;
  };
  const branchEffectOverrides = normalizeLegacyNHovBranchEffectOverrides(
    legacyState.branchEffectOverrides,
  );
  const branchExperienceByBranch = normalizeNHovBranchExperienceByBranch(
    (state as Partial<NHovState>).branchExperienceByBranch,
  );
  const effectBoundaryCaps = normalizeNHovEffectBoundaryCaps(
    (state as Partial<NHovState>).effectBoundaryCaps,
  );
  const continuousEffectProgressByBranch = {
    ...normalizeNHovContinuousEffectProgressByBranch(
      state.continuousEffectProgressByBranch,
    ),
  };
  for (const branchId of Object.keys(branchEffectOverrides)) {
    continuousEffectProgressByBranch[
      branchId as keyof typeof continuousEffectProgressByBranch
    ] = true;
  }
  return {
    count: state.count,
    branchExperienceByBranch:
      Object.keys(branchEffectOverrides).length === 0
        ? branchExperienceByBranch
        : migrateLegacyNHovBranchEffectOverrides({
            branchExperienceByBranch,
            effectBoundaryCaps,
            branchEffectOverrides,
          }),
    effectBoundaryCaps,
    ...(Object.keys(continuousEffectProgressByBranch).length === 0
      ? {}
      : { continuousEffectProgressByBranch }),
  };
}

function normalizeOHovState(
  state: OHovState,
  normalizedHov: HugeNumber,
  fallbackNowMs: number,
): OHovState {
  return {
    ...state,
    isUnlocked:
      (state as Partial<OHovState>).isUnlocked === true ||
      state.count > 0 ||
      isOHovUnlocked(normalizedHov),
    power: deserializeHuge((state as Partial<OHovState>).power),
    hovCore: deserializeHuge(state.hovCore),
    records: normalizeOHovRunRecords(
      (state as Partial<OHovState>).records,
      fallbackNowMs,
    ),
  };
}

function normalizeHovGainUnlockCosts(costs: unknown): Array<HugeNumber | null> {
  if (!Array.isArray(costs)) {
    return createDefaultHovGainUnlockCosts();
  }
  return Array.from({ length: HOV_CELL_COUNT }, (_, index) => {
    const cost = costs[index];
    if (cost === null || cost === undefined) return null;
    const normalizedCost = deserializeHuge(cost);
    return isNonNegativeHuge(normalizedCost) ? normalizedCost : null;
  });
}

// HG milestone records grow without a fixed bound, so length is preserved
// rather than padded to a known cell count.
function normalizeHovGainMilestoneTriggerCosts(
  costs: unknown,
): Array<HugeNumber | null> {
  if (!Array.isArray(costs)) {
    return [];
  }
  return costs.map((cost) => {
    if (cost === null || cost === undefined) return null;
    const normalizedCost = deserializeHuge(cost);
    return isNonNegativeHuge(normalizedCost) ? normalizedCost : null;
  });
}

function isPositiveHuge(value: HugeNumber): boolean {
  return compareHuge(value, 0) > 0;
}

function normalizeNonNegativeInteger(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
    return value;
  }
  return fallback;
}

function normalizeFiniteNonNegativeNumber(
  value: unknown,
  fallback: number,
): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return fallback;
  }
  return value;
}

function deriveHovGainFamilyUpgradesPurchased(
  state: HoverIdleModelState,
): number {
  const globalPurchases = state.economy.purchasedUpgrades.hovGain;
  const cellPurchases = state.economy.targetGrid.hovCells.reduce(
    (total, cell) => total + cell.purchasedUpgrades,
    0,
  );
  return Math.max(0, globalPurchases + cellPurchases);
}
